import React, { useState } from 'react';
import { Col, Row } from 'antd';
import { Text } from '../../atoms';
import { theme } from '../../theme';

import { UploadPreview } from './UploadPreview';
import { UploadStatusColumn } from './UploadStatusColumn';
import { useDropzone } from 'react-dropzone';
import { UploadData } from '../types';

import { validate, supportedFormats } from './validation';
import { appleFormatToJPG, compressImage, fileToUrl } from './utils';
import * as S from './styles';

type Props = {
  firstName?: string | null;
  photoUrl?: string;
  onDrop: (file: UploadData | undefined) => void;
};

export const UploadDropPhoto: React.FC<Props> = ({
  firstName,
  photoUrl,
  onDrop,
}) => {
  const [error, setError] = useState<string>();

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isFocused,
    isFileDialogActive,
  } = useDropzone({
    onDrop: async (acceptedFiles: File[]) => {
      onDrop(undefined);
      const error = await validate(acceptedFiles);
      if (error) {
        setError(error);
        return;
      }
      setError(undefined);

      let [file] = acceptedFiles;

      if (file.type.includes('heic') || file.type.includes('heif')) {
        file = await appleFormatToJPG(file);
      }

      const [{ photoLow, photoHigh }, url] = await Promise.all([
        compressImage(file),
        fileToUrl(file),
      ]);
      onDrop({
        photoLow,
        photoHigh,
        photoOriginal: { file, url },
      });
    },
  });

  return (
    <S.Wrapper>
      <S.DropZone
        {...getRootProps()}
        $isActive={isDragActive || isFocused || isFileDialogActive}
      >
        <input {...getInputProps()} />
        <Row gutter={107} justify="center" align="middle">
          <Col>
            <UploadStatusColumn
              status={error ? 'error' : photoUrl ? 'complete' : 'initial'}
            />
          </Col>
          <Col>
            <UploadPreview firstName={firstName} photo={photoUrl} />
          </Col>
        </Row>
      </S.DropZone>
      <Text $size={9} $color={theme.colors.grey50}>
        Maximum upload file size: 20MB. Supports: {supportedFormats}
      </Text>
      {error && (
        <div style={{ display: 'flex' }}>
          <Text $size={9} $color={theme.colors.red40}>
            {error}
          </Text>
        </div>
      )}
    </S.Wrapper>
  );
};
